/* eslint react/prop-types: [2, { ignore: ["setFocus", "onChange", "onNativeChange", "fieldName", "id", "name",
  "required", "className"] }] */
/* eslint-disable react/no-multi-comp */
import React from 'react';
import PropTypes from 'prop-types';
import isRequiredIf from '../../utils/isRequiredIf';
import FormElement from '../FormElement';
import { resolve } from '@cvent/nucleus-dynamic-css';
import { injectTestId, resolveTestId } from '@cvent/nucleus-test-automation';

/**
Display browser default input checkbox.

onChange = function(fieldName, newValue, newOptions)
**/


function getNewSelectedValues(oldSelectionValues, value) {
  const newSelectedValues = oldSelectionValues.slice(0);
  const indexInSelectedValues = newSelectedValues.indexOf(value);
  if (indexInSelectedValues !== -1) {
    newSelectedValues.splice(indexInSelectedValues, 1);
  } else {
    newSelectedValues.push(value);
  }
  return newSelectedValues;
}

export class Checkbox extends React.Component {
  static displayName = 'Checkbox';
  static propTypes = {
    ...FormElement.propTypes,
    /** Options for the checkbox list. */
    options: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
    /** Available Style Keys. */
    style: PropTypes.shape({}),
    /** for react-redux-form support */
    selectedValues: isRequiredIf(PropTypes.array, (props) => props.hasOwnProperty('onNativeChange')),
    /**
      Whether to disable all the checkboxes.
      If you only want some of the options to be disabled, add "disabled" attribute to those options instead.
    **/
    disabled: PropTypes.bool
  };
  static defaultProps = {
    required: false
  };

  constructor(props) {
    super(props);
    this.onChange = this.onChange.bind(this);
  }

  componentDidMount() {
    if (this.props.setFocus) {
      if (document) {
        document.getElementsByName(this.props.name ? this.props.name : this.props.fieldName)[0].focus();
      }
    }
  }

  onChange(index, value) {
    let newOptionList = [];
    if (this.props.hasOwnProperty('onNativeChange') && this.props.hasOwnProperty('selectedValues')) {
      this.props.onNativeChange(getNewSelectedValues(this.props.selectedValues, value));
    } else {
      if (!this.props.options.hasOwnProperty('selectedValues')) {
        const changes = { [index]: Object.assign(
          {},
          this.props.options[index],
          { checked: !this.props.options[index].checked }) };
        newOptionList = Object.assign([], this.props.options, changes);
      } else {
        newOptionList = Object.assign({}, this.props.options,
          { selectedValues: getNewSelectedValues(this.props.options.selectedValues, value) });
      }
      this.props.onChange(this.props.fieldName, value, newOptionList);
    }
  }

  render() {
    let hasSelectedValues;
    let options;
    let selectedValues;
    if (this.props.hasOwnProperty('selectedValues')) {
      hasSelectedValues = true;
      selectedValues = this.props.selectedValues;
      options = this.props.options;
    } else if (this.props.options.hasOwnProperty('selectedValues')) {
      hasSelectedValues = true;
      selectedValues = this.props.options.selectedValues;
      options = this.props.options.optionArray;
    } else {
      hasSelectedValues = false;
      options = this.props.options;
    }

    const checkboxLabelStyles = resolve(this.props, 'checkboxLabel');

    const listItems = options.map((option, index) => {
      const optionChecked = (hasSelectedValues
                            && selectedValues.indexOf(option.value) !== -1) || option.checked
                            || false;
      const elementId = this.props.name ? this.props.name + '_' + index : this.props.fieldName + '_' + index;
      const defaultDisabledStyle = (option.disabled || this.props.disabled) ? { opacity: 0.4 } : null;
      const resolvedStyle = {
        style: {
          ...defaultDisabledStyle,
          ...checkboxLabelStyles.style
        }
      };
      if (checkboxLabelStyles.className) {
        resolvedStyle.className = checkboxLabelStyles.className;
      }
      const name = this.props.name ? this.props.name : this.props.fieldName;
      return (
        <li key={elementId} {...injectTestId(`option-${elementId}`)}>
          <input
            {...resolve(this.props, option.className)}
            id={elementId}
            name={name}
            type="checkbox"
            value={index.toString()}
            checked={optionChecked}
            onChange={this.onChange.bind(null, index, option.value)}
            required={this.props.required}
            disabled={option.disabled || this.props.disabled}
          />
          <label
            {...resolvedStyle}
            htmlFor={elementId}
          >
            {option.name}
          </label>
        </li>
      );
    });

    return (
      <ul
        {...resolveTestId(this.props)}
        id={this.props.id || this.props.fieldName}
        style={this.props.style}
        className={this.props.className}>
        {listItems}
      </ul>
    );
  }
}

/**
FormElement wrapper around Checkbox. This is the default export.
**/
const CheckboxFormElement = (props) => {
  const { children, display, ...rest } = props;
  return (
    <FormElement {...rest} isFieldSet>
      <Checkbox
        {...rest}
        {...resolve(props, 'checkbox', display)}
        {...injectTestId('checkbox')}/>
      {children}
    </FormElement>
  );
};
CheckboxFormElement.propTypes = {
  /** The child content to render after the Checkbox. */
  children: PropTypes.node,
  /** Prop that defines an additional available key for the style prop below. */
  display: PropTypes.string,
  /** Available Style Keys. */
  style: PropTypes.shape({
    /** Style object applied to the Checkbox component. */
    checkbox: PropTypes.object,
    /** Additional style applied to Checkbox component (changes to match display prop) */
    horizontal: PropTypes.object
  })
};
CheckboxFormElement.defaultProps = {
  display: 'horizontal'
};
CheckboxFormElement.displayName = 'CheckboxFormElement';

/**
#### Data structure for the "options" property

```js
  [
    {name: 'optionNameA', value: 'optionValueA', checked: true, disabled: true},
    {name: 'optionNameB', value: 'optionValueB', checked: false},
    ...
  ]
```

```js
  {
    selectedValues: ['optionValueA'],
    optionArray: [
      {name: 'optionNameA', value: 'optionValueA'},
      {name: 'optionNameB', value: 'optionValueB'},
      ...
    ]
  }
```

"disabled" is an optional property. When it's set to true, the checkbox is disabled.

Note: if you choose to use the second one, values for the "value" property and in the "selectedValues"
array need to be primitive.
**/

export default CheckboxFormElement;
